import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Drawer, Empty, Progress, Select, message } from 'antd';
import { CheckCircleOutlined, DollarOutlined, DownloadOutlined, ExclamationCircleOutlined, SafetyOutlined } from '@ant-design/icons';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { useTheme } from '../../context/ThemeContext';
import apiClient from '../../services/apiClient';
import AIInsights from '../AIInsights';
import { buildClosedMonthsAccountCost, buildLastClosedMonthAccountCost, compactNumber, formatExecutivePeriod, numberFormatter, pickTemporalSeries, providerColors, providerIcons, providerNames } from './shared';
import { ExecutivePresentation } from './types';
import { customerInitials, customerLogoCandidates } from '../../utils/customerBranding';
import GcpInventoryDrawer from './GcpInventoryDrawer';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Tooltip, Legend, Filler);

interface CloudExecutiveLayoutProps {
  presentation: ExecutivePresentation;
  error?: string | null;
  selectorOptions?: Array<{ label: string; value: string }>;
  selectedValue?: string;
  onSelectChange?: (value: string) => void;
}

const CloudExecutiveLayout: React.FC<CloudExecutiveLayoutProps> = ({
  presentation,
  error,
  selectorOptions = [],
  selectedValue,
  onSelectChange
}) => {
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState(presentation.tabs[0]?.key || 'compute');
  const [isExportingHtml, setIsExportingHtml] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [drilldownKey, setDrilldownKey] = useState<string | null>(null);
  const [inventoryOpen, setInventoryOpen] = useState(false);
  const [inventoryAssetType, setInventoryAssetType] = useState<string | undefined>();
  const [inventoryProject, setInventoryProject] = useState<{ id: string; name: string } | null>(null);
  const [isExportingInventory, setIsExportingInventory] = useState(false);
  const [expandedService, setExpandedService] = useState<string | null>(null);
  const [serviceResourceState, setServiceResourceState] = useState<Record<string, {
    items: any[];
    total: number;
    loading: boolean;
    error?: string;
  }>>({});
  const [feedbackMap, setFeedbackMap] = useState<Record<string, any>>({});
  const [savingFeedbackId, setSavingFeedbackId] = useState<string | null>(null);
  const [customerLogoIndex, setCustomerLogoIndex] = useState(0);

  useEffect(() => {
    setActiveTab(presentation.tabs[0]?.key || 'compute');
    setCustomerLogoIndex(0);
  }, [presentation]);

  const activeSection = presentation.tabs.find(tab => tab.key === activeTab) || presentation.tabs[0];
  const logoCandidates = useMemo(
    () => customerLogoCandidates(presentation.customerDomain, presentation.customerLogoUrl),
    [presentation.customerDomain, presentation.customerLogoUrl]
  );
  const customerLogoUrl = logoCandidates[customerLogoIndex];
  const feedbackScope = useMemo(() => ({
    reportId: presentation.rawData?.reportId,
    cloudAccountId: presentation.rawData?.cloudAccountId,
    provider: presentation.provider
  }), [presentation.provider, presentation.rawData]);
  const buildFeedbackId = (item: any, sectionLabel?: string) => [
    presentation.provider,
    feedbackScope.reportId || feedbackScope.cloudAccountId || presentation.scopeLabel,
    sectionLabel || item.sectionLabel || activeSection?.label || 'detalhe',
    item.id || item.name,
    item.type,
    item.meta || ''
  ].filter(Boolean).join('|');
  const feedbackOptions = [
    { value: 'useful', label: 'Faz sentido' },
    { value: 'false_positive', label: 'Falso positivo' },
    { value: 'resolved', label: 'Já resolvido' },
    { value: 'snoozed', label: 'Ignorar por enquanto' }
  ];

  const inventoryAssetTypes: Record<string, string | undefined> = {
    Recursos: undefined,
    'Compute Instances': 'compute.googleapis.com/Instance',
    'Persistent Disks': 'compute.googleapis.com/Disk',
    'Cloud Storage Buckets': 'storage.googleapis.com/Bucket',
    'Cloud SQL Instances': 'sqladmin.googleapis.com/Instance',
    'GKE Clusters': 'container.googleapis.com/Cluster'
  };

  const openInventory = (assetType?: string) => {
    setInventoryProject(null);
    setInventoryAssetType(assetType);
    setInventoryOpen(true);
  };

  const openProjectInventory = (project: { projectId: string; name: string }) => {
    if (!project.projectId) return;
    setInventoryAssetType(undefined);
    setInventoryProject({ id: project.projectId, name: project.name });
    setInventoryOpen(true);
  };

  const exportInventory = async () => {
    const accountId = presentation.rawData?.cloudAccountId;
    if (!accountId) {
      message.error('Este relatório não possui uma conta conectada associada.');
      return;
    }
    setIsExportingInventory(true);
    try {
      const response = await apiClient.get(`/cloud/accounts/${encodeURIComponent(accountId)}/inventory.csv`, {
        params: { reportId: presentation.rawData?.reportId },
        responseType: 'blob'
      });
      const disposition = response.headers['content-disposition'] || '';
      const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1] || 'inventario-gcp.csv';
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      message.success('Inventário GCP exportado em CSV.');
    } catch (exportInventoryError: any) {
      message.error(exportInventoryError?.response?.data?.details || 'Não foi possível exportar o inventário.');
    } finally {
      setIsExportingInventory(false);
    }
  };
  const feedbackLabels: Record<string, string> = {
    useful: 'Faz sentido',
    false_positive: 'Falso positivo',
    resolved: 'Já resolvido',
    snoozed: 'Ignorado por enquanto'
  };
  const allDetailItems = useMemo(
    () => presentation.tabs.flatMap(tab => tab.items.map(item => ({ ...item, sectionLabel: tab.label }))),
    [presentation.tabs]
  );
  const monthlyHistoryItems = useMemo(() => {
    const temporalCosts = presentation.rawData?.temporalCosts || {};
    const months: string[] = Array.isArray(temporalCosts.months) ? temporalCosts.months : [];
    if (!months.length) return [];
    const seriesKeys = Object.keys(temporalCosts).filter(key => key !== 'months');
    const calculation = presentation.overview.monthlyAverageCalculation;
    const completedMonths = Number(calculation?.completedMonths || 0);
    const hasPartialMonth = Boolean(calculation?.excludedPartialMonth) && months.length > completedMonths;
    return months
      .map((label, index) => {
        const total = seriesKeys.reduce((sum, key) => {
          const series = temporalCosts[key];
          return sum + (Array.isArray(series) ? Number(series[index] || 0) : 0);
        }, 0);
        const isPartial = hasPartialMonth && index === months.length - 1;
        return {
          name: label,
          type: isPartial ? 'Mês em andamento • não entra na média' : 'Mês fechado • incluído na média',
          badge: numberFormatter(total, presentation.provider),
          badgeTone: isPartial ? ('info' as const) : ('success' as const),
          description: isPartial
            ? 'Custo parcial acumulado até a última coleta; ainda não é somado à média mensal.'
            : 'Custo total fechado deste mês, incluído no cálculo da média mensal atual.',
          meta: label,
          // Seria o mesmo valor já mostrado em badge, exibido de novo
          // embaixo sem nenhuma informação nova.
          cost: undefined
        };
      })
      .reverse();
  }, [presentation.rawData, presentation.overview.monthlyAverageCalculation, presentation.provider]);
  const drilldowns = useMemo(() => {
    const findItems = (patterns: RegExp[]) =>
      allDetailItems.filter(item =>
        patterns.some(pattern =>
          pattern.test(`${item.sectionLabel} ${item.name} ${item.type} ${item.badge} ${item.description}`)
        )
      );

    const securityPattern = /seguran|security|alert|compliance|governanca|governance|risco|risk|exposi|kms|cmek|acl|endpoint privado|ip p[uú]blico/i;
    const savingsItems = allDetailItems.filter(item => {
      const text = `${item.sectionLabel} ${item.name} ${item.type} ${item.badge} ${item.description} ${item.sourceLabel || ''}`;
      return Boolean(item.cost) && !securityPattern.test(text);
    });
    const strategicSavingsItems = allDetailItems.filter(item =>
      item.sectionLabel === 'Cenário não validado'
    );
    const officialGcpSavingsItems = allDetailItems.filter(item =>
      ['CUD', 'Rightsizing', 'Ociosos'].includes(item.sectionLabel) && Boolean(item.cost)
    );
    const annualSavings = Number(presentation.overview.annualSavingsEstimate || 0);
    const monthlySavings = presentation.provider === 'gcp' && annualSavings > 0
      ? annualSavings / 12
      : Number(presentation.executiveSummary.netMonthlySavings || 0) || annualSavings / 12;
    const reconciledSavingsItems = presentation.provider === 'gcp' && officialGcpSavingsItems.length
      ? officialGcpSavingsItems
      : presentation.provider === 'aws'
        ? allDetailItems.filter(item => item.sectionLabel === 'Otimização IA' && Boolean(item.cost))
        : savingsItems;
    const annualSavingsItems = reconciledSavingsItems.length
      ? reconciledSavingsItems
      : annualSavings > 0
        ? [{
          name: 'Economia potencial estimada',
          type: 'Estimativa executiva • Origem FinOps',
          badge: numberFormatter(annualSavings, presentation.provider),
          badgeTone: 'success' as const,
          description: [
            `Este valor representa a economia anual estimada para o ambiente conectado.`,
            `A conta parte de uma economia mensal estimada de ${numberFormatter(monthlySavings, presentation.provider)} multiplicada por 12 meses.`,
            `Quando houver recomendações detalhadas de CUD, rightsizing ou recursos ociosos, elas aparecerão aqui como itens individuais com recurso, evidência e ação recomendada.`
          ].join(' '),
          // Era o mesmo texto de cost, exibido de novo logo depois de type
          // sem nenhuma informação nova.
          meta: undefined,
          cost: `Mensal: ${numberFormatter(monthlySavings, presentation.provider)} • Anual: ${numberFormatter(annualSavings, presentation.provider)}`
        }]
        : [];
    const idleItems = findItems([/ocios|idle|disco|disk|volume|storage|elastic ip|ip/i]);
    const securityItems = allDetailItems.filter(item =>
      securityPattern.test(`${item.sectionLabel} ${item.name} ${item.type} ${item.badge} ${item.description} ${item.sourceLabel || ''}`)
    );
    const rightsizingItems = findItems([/right|sizing|capacity|compute|ec2|vm|virtual machine|upsizing/i]);
    const officialOptimizationItems = allDetailItems.filter(item =>
      item.sectionLabel === 'Rightsizing' || item.sectionLabel === 'Ociosos'
    );
    const commitmentItems = allDetailItems.filter(item => item.sectionLabel === 'CUD');
    const awsCoverageItems = allDetailItems.filter(item => item.sectionLabel === 'Cobertura AWS');
    const awsOfficialRightsizingItems = allDetailItems.filter(item => {
      const text = `${item.sectionLabel} ${item.name} ${item.type} ${item.meta || ''} ${item.sourceLabel || ''}`;
      return /rightsizing/i.test(text) && /AWS oficial|AWS Cost Explorer|Compute Optimizer/i.test(text);
    });
    const awsSavingsPlansItems = allDetailItems.filter(item =>
      /savings plans?/i.test(`${item.name} ${item.type} ${item.description} ${item.sourceLabel || ''}`)
    );
    const awsReservationItems = allDetailItems.filter(item =>
      /reserved instances?|reservations?/i.test(`${item.name} ${item.type} ${item.description} ${item.sourceLabel || ''}`)
    );
    const xknowOptimizationItems = allDetailItems.filter(item => item.sectionLabel === 'XKnow AI');
    const xknowRightsizingItems = allDetailItems.filter(item => {
      const text = `${item.sectionLabel} ${item.name} ${item.type} ${item.meta || ''} ${item.sourceLabel || ''}`;
      return /rightsizing/i.test(text) && !/AWS oficial|Compute Optimizer|cost-explorer-rightsizing/i.test(text);
    });
    const inventoryItems = allDetailItems.filter(item =>
      item.sectionLabel === 'Inventário' ||
      /cloud asset inventory|invent[aá]rio/i.test(`${item.sourceLabel || ''} ${item.description}`)
    );

    return {
      monthlyHistory: {
        title: 'Média mensal por período',
        subtitle: 'Custo total por mês fechado (base do cálculo da média) e o mês em andamento, quando disponível.',
        empty: 'Sem histórico mensal suficiente para este snapshot.',
        items: monthlyHistoryItems
      },
      monthlyCost: {
        title: 'Composição da média mensal',
        subtitle: 'Serviços que mais influenciam o custo médio do ambiente.',
        empty: 'Sem serviços suficientes para explicar a média mensal.',
        items: presentation.services.map(service => ({
          name: service.name,
          type: `${service.percentage.toFixed(1)}% do custo total`,
          // Era service.totalCost (soma do periodo) num card que fala em
          // "media mensal" - mesmo bug ja corrigido em Top Servicos, so que
          // nao conectado a nenhum clique hoje (ver drilldownKey: 'monthlyCost').
          badge: numberFormatter(service.averageMonthlyCost, presentation.provider),
          badgeTone: 'info' as const,
          description: `Média mensal de ${numberFormatter(service.averageMonthlyCost, presentation.provider)} no período analisado.`,
          meta: 'Serviço Cloud',
          cost: undefined
        }))
      },
      annualSavings: {
        title: presentation.provider === 'gcp' ? 'Origem da economia oficial identificada' : 'Origem da economia potencial',
        subtitle: presentation.provider === 'gcp'
          ? 'Somente recomendações oficiais do Google com estimativa financeira. Cenários de benchmark permanecem separados e não são somados.'
          : 'A soma dos itens abaixo reconcilia com os totais mensal e anual exibidos. Recomendações oficiais sobrepostas ficam separadas.',
        empty: 'Sem recomendações detalhadas para esta economia.',
        items: annualSavingsItems
      },
      monthlySavings: {
        title: presentation.provider === 'gcp' ? 'Economia oficial identificada por mês' : 'Origem da economia mensal potencial',
        subtitle: presentation.provider === 'gcp'
          ? 'Somente recomendações oficiais retornadas pelo Google. O cenário máximo por benchmark é exibido separadamente.'
          : 'Mesmas oportunidades do cenário anual, apresentadas em base mensal e sem somar recomendações oficiais sobrepostas.',
        empty: 'Sem recomendações detalhadas para esta economia.',
        items: annualSavingsItems
      },
      strategicPotential: {
        title: 'Cenário máximo ainda não validado',
        subtitle: 'Hipóteses por benchmark com base, premissa e ação necessária. Os valores são tetos teóricos, não economia identificada, e não devem ser somados às recomendações oficiais.',
        empty: 'Este relatório ainda não possui a composição detalhada do potencial estratégico.',
        items: strategicSavingsItems
      },
      officialOptimizations: {
        title: 'Otimizações oficiais',
        subtitle: 'Lista completa de rightsizing e recursos ociosos retornados pelo Google Recommender.',
        empty: 'Nenhuma otimização oficial encontrada para este snapshot.',
        items: officialOptimizationItems
      },
      commitment: {
        title: 'CUD Recommender',
        subtitle: 'Recomendações oficiais de compromisso de uso (CUD) retornadas pelo Google Recommender.',
        empty: 'Nenhuma recomendação de compromisso de uso encontrada para este snapshot.',
        items: commitmentItems
      },
      awsCoverage: {
        title: 'Cobertura da análise AWS',
        subtitle: 'Status de cada fonte oficial, período analisado e limitações conhecidas.',
        empty: 'Nenhuma informação de cobertura disponível para este snapshot.',
        items: awsCoverageItems
      },
      awsOfficialRightsizing: {
        title: 'Rightsizing oficial AWS',
        subtitle: 'Somente recomendações oficiais do Cost Explorer e Compute Optimizer.',
        empty: 'A AWS não retornou recomendações oficiais de rightsizing para este snapshot.',
        items: awsOfficialRightsizingItems
      },
      awsSavingsPlans: {
        title: 'Savings Plans',
        subtitle: 'Recomendação de compra, cobertura, utilização e economia oficial estimada.',
        empty: 'Nenhum detalhe de Savings Plans disponível para este snapshot.',
        items: awsSavingsPlansItems
      },
      awsReservations: {
        title: 'Reserved Instances',
        subtitle: 'Cobertura, utilização e recomendações de RI retornadas pela AWS.',
        empty: 'Nenhum detalhe de Reserved Instances disponível para este snapshot.',
        items: awsReservationItems
      },
      xknowRightsizing: {
        title: 'Rightsizing XKnow/CloudWatch',
        subtitle: 'Sinais próprios baseados em métricas operacionais, separados das recomendações oficiais AWS.',
        empty: 'Nenhum sinal próprio de rightsizing disponível para este snapshot.',
        items: xknowRightsizingItems
      },
      xknowOptimization: {
        title: 'XKnow AI independente',
        subtitle: 'Recomendações próprias executadas em paralelo às ferramentas nativas da cloud.',
        empty: 'A análise foi executada, mas não encontrou evidência suficiente para uma recomendação independente.',
        items: xknowOptimizationItems
      },
      rightsizing: {
        title: 'Detalhes de rightsizing',
        subtitle: 'Recursos de computação ou capacidade com possibilidade de ajuste.',
        empty: 'Nenhum detalhe de rightsizing encontrado para este snapshot.',
        items: rightsizingItems
      },
      idle: {
        title: 'Recursos ociosos',
        subtitle: 'Itens sem uso relevante ou com possibilidade de remoção/consolidação.',
        empty: 'Nenhum recurso ocioso detalhado encontrado para este snapshot.',
        items: idleItems
      },
      security: {
        title: 'Alertas de segurança',
        subtitle: 'Pontos de governança, postura operacional e compliance.',
        empty: 'Nenhum alerta de segurança detalhado encontrado para este snapshot.',
        items: securityItems
      },
      inventory: {
        title: 'Recursos inventariados',
        subtitle: 'Lista de recursos reais coletados via Cloud Asset Inventory para o escopo conectado.',
        empty: 'Nenhum recurso inventariado encontrado para este snapshot.',
        items: inventoryItems
      }
    };
  }, [allDetailItems, monthlyHistoryItems, presentation.executiveSummary.netMonthlySavings, presentation.overview.annualSavingsEstimate, presentation.provider, presentation.services]);
  const activeDrilldown = drilldownKey ? drilldowns[drilldownKey as keyof typeof drilldowns] : null;
  const projects = presentation.projects || [];
  const trend = useMemo(() => pickTemporalSeries(presentation.rawData, presentation.services.slice(0, 3)), [presentation]);
  const costChart = useMemo(() => {
    const calculation = presentation.overview.monthlyAverageCalculation;
    const completedMonths = Number(calculation?.completedMonths || 0);
    const hasPartialMonth = Boolean(calculation?.excludedPartialMonth && trend.months.length > completedMonths);
    const showComparableTrend = completedMonths >= 2;
    const visibleLength = showComparableTrend && completedMonths > 0 ? completedMonths : trend.months.length;
    const labels = trend.months.slice(0, visibleLength).map((month: string, index: number) =>
      hasPartialMonth && !showComparableTrend && index === visibleLength - 1
        ? `${month} (parcial)`
        : month
    );
    const datasets = trend.datasets.map((dataset: any) => ({
      ...dataset,
      data: Array.isArray(dataset.data) ? dataset.data.slice(0, visibleLength) : []
    }));
    return {
      labels,
      datasets,
      showComparableTrend,
      hasPartialMonth,
      title: showComparableTrend ? 'Tendência de Custos dos Principais Serviços' : 'Custos Disponíveis dos Principais Serviços',
      note: showComparableTrend && hasPartialMonth
        ? 'O mês atual parcial foi excluído da tendência para evitar comparação enganosa.'
        : hasPartialMonth
          ? 'Ainda existe apenas um mês fechado. O mês atual aparece como parcial e não representa tendência.'
          : completedMonths < 2
            ? 'A tendência será exibida quando houver pelo menos dois meses fechados comparáveis.'
            : ''
    };
  }, [presentation.overview.monthlyAverageCalculation, trend]);
  const aiInput = useMemo(
    () => ({
      ...presentation.rawData,
      provider: presentation.provider
    }),
    [presentation.provider, presentation.rawData]
  );
  const lastClosedMonthAccountCost = useMemo(
    () => buildLastClosedMonthAccountCost(presentation.rawData),
    [presentation.rawData]
  );
  // Mesmo gate do card "Custo Bruto": só a AWS tem o recorte bruto
  // (temporalCosts/topServices excluindo RECORD_TYPE=Credit) separado do
  // líquido (overview.usageCost/monthlyAverageCost) - e só vale a pena
  // mostrar quando o crédito realmente muda o quadro. Fora disso, Média
  // Mensal/Custo Total continuam no líquido de sempre (overview), igual
  // Azure/GCP, que não têm essa distinção.
  const hasMeaningfulAwsCredit =
    presentation.provider === 'aws' &&
    typeof presentation.overview.usageCost === 'number' &&
    Math.abs(presentation.overview.creditCost || 0) > 1;
  const closedMonthsAccountCostGross = useMemo(
    () => (hasMeaningfulAwsCredit ? buildClosedMonthsAccountCost(presentation.rawData) : null),
    [hasMeaningfulAwsCredit, presentation.rawData]
  );
  const donutData = useMemo(
    () => ({
      labels: presentation.services.slice(0, 5).map(item => item.name),
      datasets: [
        {
          // lastClosedMonthCost, not totalCost (soma do período) - mesma
          // base do "% do Total" da tabela logo abaixo, pra fatia visual e
          // percentual no tooltip baterem com o resto da seção.
          data: presentation.services.slice(0, 5).map(item => item.lastClosedMonthCost),
          backgroundColor: ['#ef4444', '#f59e0b', '#0ea5e9', '#10b981', '#8b5cf6'],
          borderWidth: 0
        }
      ]
    }),
    [presentation.services]
  );
  const renderAffectedResources = (item: any) => {
    if (!item.affectedResources?.length) return null;

    return (
      <div className="cloud-exec__affected-list">
        <div className="cloud-exec__affected-title">Recursos afetados</div>
        {item.affectedResources.slice(0, 8).map((resource: Record<string, any>, index: number) => (
          <div key={`${resource.name || 'resource'}-${index}`} className="cloud-exec__affected-item">
            <strong>{resource.name || 'Recurso afetado'}</strong>
            <span>{[resource.type, resource.location, resource.issue].filter(Boolean).join(' • ')}</span>
            {resource.recommendation && <em>{resource.recommendation}</em>}
          </div>
        ))}
        {item.affectedResources.length > 8 && (
          <div className="cloud-exec__affected-more">
            +{item.affectedResources.length - 8} recurso(s) afetado(s)
          </div>
        )}
      </div>
    );
  };

  const serviceResourceSourceLabel = (source?: string) => {
    switch (source) {
      case 'cost-explorer':
        return 'Cost Explorer';
      case 'cost-management':
        return 'Cost Management';
      case 'billing-export':
        return 'Billing Export';
      case 'estimated':
        return 'Estimado';
      default:
        return 'Inventário';
    }
  };

  const toggleServiceResources = async (service: any) => {
    if (expandedService === service.name) {
      setExpandedService(null);
      return;
    }
    setExpandedService(service.name);
    if (serviceResourceState[service.name]?.items?.length || serviceResourceState[service.name]?.loading) return;

    const accountId = presentation.rawData?.cloudAccountId;
    if (presentation.provider !== 'gcp' || !accountId) return;
    setServiceResourceState(current => ({
      ...current,
      [service.name]: { items: [], total: 0, loading: true }
    }));
    try {
      const response = await apiClient.get(`/cloud/accounts/${encodeURIComponent(accountId)}/inventory`, {
        params: {
          reportId: presentation.rawData?.reportId,
          serviceGroup: service.name,
          page: 1,
          limit: 30,
          sortBy: 'monthlyCost',
          sortOrder: 'desc'
        }
      });
      setServiceResourceState(current => ({
        ...current,
        [service.name]: {
          items: response.data?.items || [],
          total: Number(response.data?.pagination?.total || 0),
          loading: false
        }
      }));
    } catch (requestError: any) {
      setServiceResourceState(current => ({
        ...current,
        [service.name]: {
          items: [],
          total: 0,
          loading: false,
          error: requestError?.response?.data?.details || 'Não foi possível consultar os recursos deste serviço.'
        }
      }));
    }
  };

  const renderServiceResources = (service: any) => {
    if (!service) return null;

    const remoteState = serviceResourceState[service.name];
    const resources = remoteState
      ? remoteState.items
      : Array.isArray(service.resources) ? service.resources : [];
    const resourceCount = remoteState ? remoteState.total : Number(service.resourceCount || resources.length);

    return (
      <div className="cloud-exec__service-resources">
        <div className="cloud-exec__service-resources-head">
          <div>
            <strong>Camada inferior de {service.name}</strong>
            <span>
              {remoteState?.loading
                ? 'Consultando o inventário deste serviço...'
                : resources.length
                ? resourceCount > resources.length
                  ? `Exibindo ${resources.length} de ${resourceCount} recursos reais associados ao serviço nesta coleta.`
                  : `${resources.length} recurso(s) real(is) associado(s) ao serviço nesta coleta.`
                : 'Ainda não há recursos inventariados para este serviço no snapshot.'}
            </span>
          </div>
          <span className="cloud-exec__service-source">
            Custo do serviço: {presentation.provider === 'aws'
              ? 'Cost Explorer'
              : presentation.provider === 'azure'
                ? 'Cost Management'
                : 'Billing Export'}
          </span>
        </div>

        {remoteState?.loading ? (
          <div className="cloud-exec__empty">Carregando recursos do inventário...</div>
        ) : remoteState?.error ? (
          <Alert type="error" showIcon message="Falha ao carregar recursos" description={remoteState.error} />
        ) : resources.length > 0 ? (
          <div className="cloud-exec__service-resource-list">
            {resources.map((resource: any, index: number) => (
              <div key={`${service.name}-${resource.name}-${index}`} className="cloud-exec__service-resource-item">
                <div>
                  <strong>{resource.name}</strong>
                  <span>{[resource.type || resource.assetType, resource.status, resource.region || resource.location].filter(Boolean).join(' • ')}</span>
                  {resource.note && <em>{resource.note}</em>}
                </div>
                <div className="cloud-exec__service-resource-cost">
                  {typeof resource.monthlyCost === 'number'
                    ? numberFormatter(resource.monthlyCost, presentation.provider)
                    : 'Sem custo individual'}
                  <small>{serviceResourceSourceLabel(resource.costSource)}</small>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="cloud-exec__empty">
            Sem custo individual para estes recursos neste snapshot. Quando o export detalhado por recurso tiver match, o valor aparece aqui.
          </div>
        )}
      </div>
    );
  };
  const chartTextColor = isDark ? '#e2e8f0' : '#334155';
  const chartGridColor = isDark ? 'rgba(148, 163, 184, 0.16)' : 'rgba(148, 163, 184, 0.18)';
  const periodLabel = useMemo(() => formatExecutivePeriod(presentation.rawData), [presentation.rawData]);
  const generatedAt = useMemo(
    () =>
      new Date().toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      }),
    []
  );
  useEffect(() => {
    const loadFeedback = async () => {
      if (!feedbackScope.reportId && !feedbackScope.cloudAccountId) {
        setFeedbackMap({});
        return;
      }

      try {
        const response = await apiClient.get('/finding-feedback', {
          params: {
            reportId: feedbackScope.reportId,
            cloudAccountId: feedbackScope.reportId ? undefined : feedbackScope.cloudAccountId
          }
        });
        const nextMap = (response.data?.feedback || []).reduce((acc: Record<string, any>, item: any) => {
          acc[item.findingId] = item;
          return acc;
        }, {});
        setFeedbackMap(nextMap);
      } catch (error) {
        console.error('Erro ao carregar feedback manual:', error);
      }
    };

    loadFeedback();
  }, [feedbackScope.reportId, feedbackScope.cloudAccountId]);
  const handleFeedbackChange = async (item: any, status: string, sectionLabel?: string) => {
    const findingId = buildFeedbackId(item, sectionLabel);
    setSavingFeedbackId(findingId);

    try {
      const response = await apiClient.post('/finding-feedback', {
        provider: presentation.provider,
        reportId: feedbackScope.reportId,
        cloudAccountId: feedbackScope.cloudAccountId,
        findingId,
        findingType: sectionLabel || item.sectionLabel || item.type,
        title: item.name,
        status,
        context: {
          type: item.type,
          badge: item.badge,
          description: item.description,
          cost: item.cost,
          scopeLabel: presentation.scopeLabel
        }
      });
      setFeedbackMap(current => ({
        ...current,
        [findingId]: response.data?.feedback || {
          findingId,
          status
        }
      }));
      message.success('Feedback registrado para aprendizado da IA.');
    } catch (error) {
      message.error('Não foi possível salvar o feedback agora.');
    } finally {
      setSavingFeedbackId(null);
    }
  };
  const renderFeedbackControl = (item: any, sectionLabel?: string) => {
    if (item?.allowFeedback === false) return null;
    const findingId = buildFeedbackId(item, sectionLabel);
    const feedback = feedbackMap[findingId];

    return (
      <div className="cloud-exec__feedback">
        <div>
          <span className="cloud-exec__feedback-label">Feedback para IA</span>
          {feedback?.status && (
            <span className={`cloud-exec__feedback-status is-${feedback.status}`}>
              {feedbackLabels[feedback.status] || feedback.status}
            </span>
          )}
        </div>
        <Select
          size="small"
          value={feedback?.status}
          placeholder="Avaliar recomendação"
          options={feedbackOptions}
          loading={savingFeedbackId === findingId}
          onChange={(value) => handleFeedbackChange(item, value, sectionLabel)}
          style={{ minWidth: 190 }}
        />
      </div>
    );
  };
  const handleExportHtml = async () => {
    setIsExportingHtml(true);
    setExportError(null);

    try {
      const payload = {
        provider: presentation.provider,
        cloudAccountId: presentation.rawData?.cloudAccountId,
        providerLabel: presentation.providerLabel,
        customerLabel: presentation.customerLabel,
        customerDomain: presentation.customerDomain,
        customerLogoUrl: presentation.customerLogoUrl,
        scopeLabel: presentation.scopeLabel,
        periodLabel,
        generatedAt,
        overview: presentation.overview,
        executiveSummary: presentation.executiveSummary,
        summaryMetrics: presentation.summaryMetrics.map(({ title, count, subtitle, value }) => ({
          title,
          count,
          subtitle,
          value
        })),
        services: presentation.services,
        tabs: presentation.tabs
      };

      const response = await apiClient.post('/reports/executive-html', payload, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const safeName = presentation.customerLabel.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'cliente';
      link.href = url;
      link.download = `finops-${safeName}-${new Date().toISOString().slice(0, 10)}.html`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setExportError('Não foi possível gerar o HTML executivo neste momento.');
    } finally {
      setIsExportingHtml(false);
    }
  };

  return (
    <div className={`cloud-exec${isDark ? ' dark' : ''}`}>
      <div className="cloud-exec__header">
        <div className="cloud-exec__header-top">
          <div className="cloud-exec__brand">
            <div className="cloud-exec__brand-mark" style={{ background: providerColors[presentation.provider] }}>
              {customerLogoUrl ? (
                <img
                  src={customerLogoUrl}
                  alt={`Logo ${presentation.customerLabel}`}
                  onError={() => setCustomerLogoIndex(index => index + 1)}
                />
              ) : presentation.customerDomain || presentation.customerLabel ? (
                <span className="cloud-exec__brand-initials">
                  {customerInitials(presentation.customerLabel, presentation.customerDomain)}
                </span>
              ) : (
                providerIcons[presentation.provider]
              )}
            </div>
            <div>
              <div className="cloud-exec__eyebrow">{providerNames[presentation.provider]}</div>
              <div className="cloud-exec__period">Período: {periodLabel}</div>
              <div className="cloud-exec__title">{presentation.customerLabel}</div>
              <div className="cloud-exec__subtitle">
                Visão executiva de custos, inventário, riscos e oportunidades de otimização para o ambiente conectado.
              </div>
            </div>
          </div>

          <div className="cloud-exec__meta">
            <div className="cloud-exec__meta-card">
              <div className="cloud-exec__meta-label">Provider</div>
              <div className="cloud-exec__meta-value">{presentation.providerLabel}</div>
            </div>
            <div className="cloud-exec__meta-card">
              <div className="cloud-exec__meta-label">Escopo</div>
              <div className="cloud-exec__meta-value">{presentation.scopeLabel}</div>
            </div>
            <div className="cloud-exec__meta-card">
              <div className="cloud-exec__meta-label">Data de Geração</div>
              <div className="cloud-exec__meta-value">{generatedAt}</div>
            </div>
          </div>
        </div>

        <div className="cloud-exec__controls">
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            className="cloud-exec__export-button"
            onClick={handleExportHtml}
            loading={isExportingHtml}
          >
            Exportar HTML
          </Button>

          {!!onSelectChange && !!selectorOptions.length && (
            <Select
              value={selectedValue || undefined}
              onChange={onSelectChange}
              placeholder={presentation.selectorPlaceholder}
              style={{ minWidth: 320 }}
              options={selectorOptions}
            />
          )}
        </div>
      </div>

      <div className="cloud-exec__print-summary">
        <div className="cloud-exec__print-title">Relatório Executivo FinOps</div>
        <div className="cloud-exec__print-grid">
          <div><strong>Período:</strong> {periodLabel}</div>
          <div><strong>Cliente:</strong> {presentation.customerLabel}</div>
          <div><strong>Provider:</strong> {presentation.providerLabel}</div>
          <div><strong>Escopo:</strong> {presentation.scopeLabel}</div>
          <div><strong>Gerado em:</strong> {generatedAt}</div>
        </div>
      </div>

      {error && (
        <Alert
          type="warning"
          showIcon
          className="cloud-exec__alert"
          style={{ marginBottom: 16 }}
          message={error}
        />
      )}

      {exportError && (
        <Alert
          type="error"
          showIcon
          className="cloud-exec__alert"
          style={{ marginBottom: 16 }}
          message={exportError}
        />
      )}

      {presentation.rawData?.metadata?.analysisStatus === 'reconciled-from-previous-inventory' && (
        <Alert
          type="info"
          showIcon
          className="cloud-exec__alert"
          style={{ marginBottom: 16 }}
          message="Custos atualizados; análise técnica preservada"
          description="A economia financeira foi reconciliada com os custos atuais, mas recursos, métricas e recomendações técnicas permanecem baseados na última coleta completa. Use Atualização completa para recoletar a análise técnica."
        />
      )}

      <div className="cloud-exec__insights-section" style={{ marginBottom: 24 }}>
        <AIInsights subscriptionData={aiInput} />
      </div>

      <div className="cloud-exec__overview cloud-exec__block-avoid-break">
        <button
          type="button"
          className="cloud-exec__metric-card cloud-exec__drill-card"
          onClick={() => setDrilldownKey('monthlyHistory')}
        >
          <div className="cloud-exec__metric-label">Media Mensal</div>
          <div className="cloud-exec__metric-value cost">
            {numberFormatter(
              closedMonthsAccountCostGross?.average ?? presentation.overview.monthlyAverageCost,
              presentation.provider
            )}
          </div>
          <div className="cloud-exec__metric-help">
            {presentation.overview.monthlyAverageCalculation?.completedMonths
              ? `Média de ${presentation.overview.monthlyAverageCalculation.completedMonths} meses fechados; mês atual não incluído.` +
                (closedMonthsAccountCostGross ? ' Valor bruto, antes de créditos.' : '')
              : 'Sem mês fechado disponível; exibindo o custo parcial ou projetado atual.'}
          </div>
          <div className="cloud-exec__drill-hint">Clique para ver por período</div>
        </button>

        <button
          type="button"
          className="cloud-exec__metric-card cloud-exec__drill-card"
          onClick={() => setDrilldownKey('monthlyHistory')}
        >
          <div className="cloud-exec__metric-label">Custo Total (Meses Fechados)</div>
          <div className="cloud-exec__metric-value cost">
            {numberFormatter(
              closedMonthsAccountCostGross?.total ??
                presentation.overview.monthlyAverageCost * Math.max(presentation.overview.monthlyAverageCalculation?.completedMonths || 0, 1),
              presentation.provider
            )}
          </div>
          <div className="cloud-exec__metric-help">
            {presentation.overview.monthlyAverageCalculation?.completedMonths
              ? `Soma de ${presentation.overview.monthlyAverageCalculation.completedMonths} meses fechados; mês atual não incluído.` +
                (closedMonthsAccountCostGross ? ' Valor bruto, antes de créditos.' : '')
              : 'Sem mês fechado disponível; exibindo o custo parcial ou projetado atual.'}
          </div>
          <div className="cloud-exec__drill-hint">Clique para ver por período</div>
        </button>

        {
          // So a AWS tem um conceito de credito que o cliente consegue conferir
          // de forma equivalente (Billing -> Credits, redeemable, com o mesmo
          // recorte "ultimo mes fechado" que usageCost/creditCost usam la). No GCP
          // isso vira confusao: o "credito" e desconto automatico (uso
          // comprometido/sustentado) ja embutido no total que o proprio console
          // do Google mostra - nao existe uma tela separada de "creditos
          // ativos" pra bater, e o periodo de usageCost/creditCost do GCP e o
          // acumulado de todos os meses do relatorio, nao um mes so. Um
          // cliente Vidalink reportou exatamente essa confusao comparando
          // contra o proprio console GCP (2026-09-25) - ver
          // project_vidalink_billing_reconciliation.md.
          hasMeaningfulAwsCredit && (
          <div className="cloud-exec__metric-card">
            <div className="cloud-exec__metric-label">Custo Bruto (antes de créditos)</div>
            <div className="cloud-exec__metric-value cost">
              {numberFormatter(presentation.overview.usageCost || 0, presentation.provider)}
            </div>
            <div className="cloud-exec__metric-help">
              {`Créditos aplicados: ${numberFormatter(Math.abs(presentation.overview.creditCost || 0), presentation.provider)}. ` +
                'Um custo líquido baixo pode esconder uso real coberto por crédito promocional/compromisso - créditos não são permanentes.'}
            </div>
          </div>
        )}

        {lastClosedMonthAccountCost && (
          <button
            type="button"
            className="cloud-exec__metric-card cloud-exec__drill-card"
            onClick={() => setDrilldownKey('monthlyHistory')}
          >
            <div className="cloud-exec__metric-label">Mês Anterior</div>
            <div className="cloud-exec__metric-value cost">
              {numberFormatter(lastClosedMonthAccountCost.value, presentation.provider)}
            </div>
            <div className="cloud-exec__metric-help">
              {lastClosedMonthAccountCost.label
                ? `Custo apenas de ${lastClosedMonthAccountCost.label}, o último mês fechado.`
                : 'Custo apenas do último mês fechado.'}
            </div>
            <div className="cloud-exec__drill-hint">Clique para ver por período</div>
          </button>
        )}

        {presentation.provider === 'gcp' && presentation.overview.currentMonthEstimate && (
          <div className="cloud-exec__metric-card">
            <div className="cloud-exec__metric-label">Mês Atual (Projeção)</div>
            <div className="cloud-exec__metric-value cost">
              {numberFormatter(presentation.overview.currentMonthEstimate.projectedCost, presentation.provider)}
            </div>
            <div className="cloud-exec__metric-help">
              {`Realizado até agora: ${numberFormatter(presentation.overview.currentMonthEstimate.partialCost, presentation.provider)} ` +
                `(${presentation.overview.currentMonthEstimate.daysElapsed} de ${presentation.overview.currentMonthEstimate.daysInMonth} dias). ` +
                (presentation.overview.currentMonthEstimate.method === 'ai-forecast'
                  ? 'Projeção própria via modelo de IA (tendência/sazonalidade), não é um número oficial do provedor.'
                  : 'Projeção linear própria, não é um número oficial do provedor.')}
            </div>
          </div>
        )}

        <button
          type="button"
          className="cloud-exec__metric-card cloud-exec__drill-card"
          onClick={() => setDrilldownKey('monthlySavings')}
        >
          <div className="cloud-exec__metric-label">
            {presentation.provider === 'gcp' ? 'Economia Oficial Identificada Mensal' : 'Potencial de Economia Mensal'}
          </div>
          <div className="cloud-exec__metric-value savings">
            {numberFormatter(
              Number(presentation.executiveSummary.netMonthlySavings || 0),
              presentation.provider
            )}
          </div>
          <div className="cloud-exec__metric-help">
            {presentation.provider === 'gcp'
              ? 'Somente recomendações oficiais do Google com estimativa financeira.'
              : 'Mesma composição do cenário anual, dividida por 12 meses.'}
          </div>
          <div className="cloud-exec__drill-hint">Clique para ver as recomendações</div>
        </button>

        <button
          type="button"
          className="cloud-exec__metric-card cloud-exec__drill-card"
          onClick={() => setDrilldownKey('annualSavings')}
        >
          <div className="cloud-exec__metric-label">
            {presentation.provider === 'gcp' ? 'Economia Oficial Identificada Anual' : 'Potencial de Economia Anual'}
          </div>
          <div className="cloud-exec__metric-value savings">
            {numberFormatter(
              presentation.provider === 'gcp'
                ? Number(presentation.executiveSummary.netMonthlySavings || 0) * 12
                : presentation.overview.annualSavingsEstimate,
              presentation.provider
            )}
          </div>
          <div className="cloud-exec__metric-help">
            {presentation.provider === 'gcp'
              ? 'Projeção anual das recomendações oficiais mensais; sujeita à validação e continuidade da elegibilidade.'
              : 'Oportunidades mapeadas pelo sistema atual.'}
          </div>
          <div className="cloud-exec__drill-hint">Clique para ver as oportunidades</div>
        </button>
      </div>

      {presentation.provider === 'gcp' && presentation.rawData?.billingScopeComparison && (
        <Alert
          style={{ marginBottom: 24 }}
          type={Number(presentation.rawData.billingScopeComparison.scopeCoveragePercent || 0) < 95 ? 'warning' : 'info'}
          showIcon
          message="Cobertura do escopo de faturamento"
          description={
            `Projetos da organização: ${numberFormatter(presentation.rawData.billingScopeComparison.scopedMonthlyAverage, presentation.provider)} por mês ` +
            `(${Number(presentation.rawData.billingScopeComparison.scopeCoveragePercent || 0).toFixed(1)}% da billing account). ` +
            `Billing account completa: ${numberFormatter(presentation.rawData.billingScopeComparison.billingMonthlyAverage, presentation.provider)} por mês, ` +
            `com ${numberFormatter(presentation.rawData.billingScopeComparison.outsideScopeMonthlyAverage, presentation.provider)} fora dos projetos selecionados.`
          }
        />
      )}

      {presentation.provider === 'gcp' && !presentation.rawData?.billingScopeComparison && presentation.rawData?.billingScopeComparisonError && (
        <Alert
          style={{ marginBottom: 24 }}
          type="warning"
          showIcon
          message="Cobertura da billing account ainda não confirmada"
          description="Os custos exibidos consideram somente os projetos selecionados da organização. A consulta da billing account completa falhou nesta coleta; portanto, o valor mensal não deve ser interpretado como o total integral da fatura."
        />
      )}

      <section className="cloud-exec__section cloud-exec__print-section">
        <div className="cloud-exec__section-head">
          <div>
            <h2 className="cloud-exec__section-title">Oportunidades de Economia Identificadas</h2>
            <div className="cloud-exec__section-subtitle">
              Priorização de ações com origem dos dados indicada entre coleta real, métricas operacionais e estimativas.
            </div>
          </div>
        </div>

        <div className="cloud-exec__grid-3">
          {presentation.summaryMetrics.map(metric => {
            const metricTitle = metric.title.toLowerCase();
            const metricKey = metric.drilldownKey || (metricTitle.includes('inventari')
              ? 'inventory'
              : metricTitle.includes('seguran')
              ? 'security'
              : metricTitle.includes('cud') || metricTitle.includes('commit')
              ? 'annualSavings'
              : metricTitle.includes('otimiza')
                ? 'annualSavings'
              : metricTitle.includes('ocioso') || metricTitle.includes('volume') || metricTitle.includes('disco')
                ? 'idle'
                : 'rightsizing');

            return (
            <button
              key={metric.title}
              type="button"
              className="cloud-exec__opportunity-card cloud-exec__drill-card"
              style={{ ['--accent' as any]: metric.accent }}
              onClick={() => {
                if (metricKey === 'inventory') {
                  setInventoryOpen(true);
                  return;
                }
                setDrilldownKey(metricKey);
              }}
            >
              <div className="cloud-exec__opportunity-head">
                <div>
                  <div className="cloud-exec__opportunity-title">{metric.title}</div>
                  <div className="cloud-exec__opportunity-caption">{metric.subtitle}</div>
                </div>
                <div className="cloud-exec__icon" style={{ background: metric.accent }}>
                  {metric.icon}
                </div>
              </div>
              <div className="cloud-exec__opportunity-number">{typeof metric.count === 'number' ? compactNumber(metric.count) : metric.count}</div>
              {typeof metric.value === 'number' && metric.value > 0 && (
                <div className="cloud-exec__opportunity-number cloud-exec__opportunity-number--secondary savings">
                  {numberFormatter(metric.value, presentation.provider)}
                </div>
              )}
              <div className="cloud-exec__drill-hint">Clique para detalhar</div>
            </button>
          )})}
        </div>
      </section>

      {projects.length > 0 && (
        <section className="cloud-exec__section cloud-exec__print-section">
          <div className="cloud-exec__section-head">
            <div>
              <h2 className="cloud-exec__section-title">Custos por Projeto</h2>
              <div className="cloud-exec__section-subtitle">
                Distribuição financeira dos projetos conectados neste escopo GCP. Clique em um projeto para ver seus recursos.
              </div>
            </div>
          </div>

          <div className="cloud-exec__panel">
            <div className="cloud-exec__project-highlight">
              <strong>
                {projects[0].name} concentra {projects[0].percentage.toFixed(1)}% do custo analisado.
              </strong>
              <span>
                Use este ranking para priorizar investigacao por squad, ambiente e servico principal.
              </span>
            </div>
            <div className="cloud-exec__table-wrap">
              <table className="cloud-exec__table">
                <thead>
                  <tr>
                    <th>Projeto</th>
                    <th>Project ID</th>
                    <th>Principal servico</th>
                    <th className="numeric">Último Mês Fechado</th>
                    <th className="numeric">Media Mensal</th>
                    <th className="numeric">% do Total</th>
                    <th className="numeric">Peso</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((project, index) => (
                    <tr
                      key={project.projectId || project.name}
                      className={project.projectId ? 'cloud-exec__clickable-row' : undefined}
                      role={project.projectId ? 'button' : undefined}
                      tabIndex={project.projectId ? 0 : undefined}
                      onClick={() => openProjectInventory(project)}
                      onKeyDown={event => {
                        if (project.projectId && (event.key === 'Enter' || event.key === ' ')) {
                          event.preventDefault();
                          openProjectInventory(project);
                        }
                      }}
                    >
                      <td>
                        <strong>{index + 1}. {project.name}</strong>
                      </td>
                      <td>{project.projectId || '-'}</td>
                      <td>{project.topService || '-'}</td>
                      <td className="numeric">{numberFormatter(project.lastClosedMonthCost ?? project.totalCost, presentation.provider)}</td>
                      <td className="numeric">{numberFormatter(project.averageMonthlyCost, presentation.provider)}</td>
                      <td className="numeric">{project.percentage.toFixed(1)}%</td>
                      <td className="numeric">
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                          <div className="cloud-exec__progress">
                            <div className="cloud-exec__progress-bar" style={{ width: `${Math.min(project.percentage, 100)}%` }} />
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {presentation.rawData?.financialReconciliation && (
              <div className="gcp-inventory__reconciliation" style={{ marginTop: 16, marginBottom: 0 }}>
                <div className="gcp-inventory__reconciliation-head">
                  <div>
                    <strong>Reconciliação financeira</strong>
                    <span>{presentation.rawData.financialReconciliation.periodLabel}</span>
                  </div>
                  <span className={`cloud-exec__badge ${presentation.rawData.financialReconciliation.status === 'reconciled' ? 'success' : 'danger'}`}>
                    {presentation.rawData.financialReconciliation.status === 'reconciled' ? 'Reconciliado' : 'Divergente'}
                  </span>
                </div>
                <div className="cloud-exec__resource-description">
                  Total oficial: {numberFormatter(presentation.rawData.financialReconciliation.officialPeriodCost, presentation.provider)}
                  {' • '}Export detalhado: {numberFormatter(presentation.rawData.financialReconciliation.resourceExportPeriodCost, presentation.provider)}
                  {presentation.rawData.financialReconciliation.allocationStatus !== 'requires-full-inventory-refresh' && <>
                    {' • '}Atribuído: {numberFormatter(presentation.rawData.financialReconciliation.assignedPeriodCost, presentation.provider)}
                    {' • '}Não atribuído: {numberFormatter(presentation.rawData.financialReconciliation.unattributedPeriodCost, presentation.provider)}
                  </>}
                </div>
                {presentation.rawData.financialReconciliation.allocationStatus !== 'requires-full-inventory-refresh'
                  ? <Progress percent={Number(presentation.rawData.financialReconciliation.coveragePercent || 0)} size="small" />
                  : <div className="cloud-exec__resource-description">A cobertura por recurso será recalculada na próxima coleta completa.</div>}
              </div>
            )}
          </div>
        </section>
      )}

      <section className="cloud-exec__section cloud-exec__print-section cloud-exec__print-break-before">
        <div className="cloud-exec__section-head">
          <div>
            <h2 className="cloud-exec__section-title">Top Serviços por Custo</h2>
            <div className="cloud-exec__section-subtitle">Ranking financeiro e tendência dos principais vetores de custo.</div>
          </div>
        </div>

        <div className="cloud-exec__service-grid">
          {presentation.services.slice(0, 3).map((service, index) => (
            <button
              key={service.name}
              type="button"
              className={`cloud-exec__service-card ${expandedService === service.name ? 'expanded' : ''}`}
              onClick={() => toggleServiceResources(service)}
            >
              <div className="cloud-exec__rank">{index + 1}</div>
              <div className="cloud-exec__service-name">{service.name}</div>
              <div className="cloud-exec__service-cost">
                {numberFormatter(service.lastClosedMonthCost ?? service.totalCost, presentation.provider)}
              </div>
              <div className="cloud-exec__service-meta">
                Último mês fechado
                {service.closedMonthlyBreakdown?.length ? ` (${service.closedMonthlyBreakdown[service.closedMonthlyBreakdown.length - 1].month})` : ''}
              </div>
              <div className="cloud-exec__service-meta">
                Media: {numberFormatter(service.averageMonthlyCost, presentation.provider)} por mes
              </div>
              <div className="cloud-exec__service-meta">{service.percentage.toFixed(1)}% do total</div>
              {service.closedMonthlyBreakdown && service.closedMonthlyBreakdown.length > 1 && (
                <div className="cloud-exec__service-history">
                  {service.closedMonthlyBreakdown.slice(-3).map(entry => (
                    <span key={entry.month} className="cloud-exec__service-history-item">
                      {entry.month}: {numberFormatter(entry.value, presentation.provider)}
                    </span>
                  ))}
                </div>
              )}
              <div className="cloud-exec__drill-hint">
                {expandedService === service.name ? 'Ocultar camada inferior' : 'Expandir recursos'}
              </div>
            </button>
          ))}
        </div>

        {expandedService && (
          <div className="cloud-exec__panel cloud-exec__service-expanded-panel">
            {renderServiceResources(presentation.services.find(service => service.name === expandedService))}
          </div>
        )}

        <div className="cloud-exec__grid-2">
          <div className="cloud-exec__panel">
            <h3 style={{ marginBottom: 8 }}>{costChart.title}</h3>
            {costChart.note && <div className="cloud-exec__section-subtitle" style={{ marginBottom: 16 }}>{costChart.note}</div>}
            <div style={{ height: 320 }}>
              {costChart.datasets.length > 0 ? (
                costChart.showComparableTrend ? <Line
                  data={{ labels: costChart.labels, datasets: costChart.datasets as any }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'top',
                        labels: { color: chartTextColor }
                      }
                    },
                    scales: {
                      x: {
                        ticks: { color: chartTextColor },
                        grid: { color: chartGridColor }
                      },
                      y: {
                        ticks: {
                          color: chartTextColor,
                          callback: value => compactNumber(Number(value))
                        },
                        grid: { color: chartGridColor }
                      }
                    }
                  }}
                /> : <Bar
                  data={{ labels: costChart.labels, datasets: costChart.datasets as any }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: 'top', labels: { color: chartTextColor } },
                      tooltip: {
                        callbacks: {
                          label: context => `${context.dataset.label}: ${numberFormatter(Number(context.raw || 0), presentation.provider)}`
                        }
                      }
                    },
                    scales: {
                      x: { ticks: { color: chartTextColor }, grid: { color: chartGridColor } },
                      y: {
                        beginAtZero: true,
                        ticks: { color: chartTextColor, callback: value => compactNumber(Number(value)) },
                        grid: { color: chartGridColor }
                      }
                    }
                  }}
                />
              ) : (
                <div className="cloud-exec__empty">Sem historico temporal suficiente para o grafico.</div>
              )}
            </div>
          </div>

          <div className="cloud-exec__panel">
            <h3 style={{ marginBottom: 16 }}>Distribuição por Serviço</h3>
            <div style={{ height: 320 }}>
              {presentation.services.length > 0 ? (
                <Doughnut
                  data={donutData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom',
                        labels: { color: chartTextColor }
                      },
                      tooltip: {
                        callbacks: {
                          // Percentual apenas, sem valor em R$ - e usa o
                          // MESMO service.percentage ja calculado pelo
                          // backend sobre todos os servicos da conta (nao
                          // so os 5 do donut), pra bater com a coluna
                          // "% do Total" da tabela logo abaixo.
                          label: context => {
                            const service = presentation.services[context.dataIndex];
                            const percent = service?.percentage ?? 0;
                            return `${context.label}: ${percent.toFixed(1)}%`;
                          }
                        }
                      }
                    }
                  }}
                />
              ) : (
                <div className="cloud-exec__empty">Sem servicos para distribuir.</div>
              )}
            </div>
          </div>
        </div>

        <div className="cloud-exec__panel" style={{ marginTop: 16 }}>
          <div className="cloud-exec__table-wrap">
            <table className="cloud-exec__table">
              <thead>
                <tr>
                  <th>Serviço</th>
                  <th className="numeric">Último Mês Fechado</th>
                  <th className="numeric">Media Mensal</th>
                  <th className="numeric">% do Total</th>
                  <th className="numeric">Peso</th>
                  <th className="numeric">Detalhe</th>
                </tr>
              </thead>
              <tbody>
                {presentation.services.map(service => (
                  <Fragment key={service.name}>
                    <tr>
                      <td>{service.name}</td>
                      <td className="numeric">{numberFormatter(service.lastClosedMonthCost ?? service.totalCost, presentation.provider)}</td>
                      <td className="numeric">{numberFormatter(service.averageMonthlyCost, presentation.provider)}</td>
                      <td className="numeric">{service.percentage.toFixed(1)}%</td>
                      <td className="numeric">
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                          <div className="cloud-exec__progress">
                            <div className="cloud-exec__progress-bar" style={{ width: `${Math.min(service.percentage, 100)}%` }} />
                          </div>
                        </div>
                      </td>
                      <td className="numeric">
                        <Button size="small" onClick={() => toggleServiceResources(service)}>
                          {expandedService === service.name ? 'Ocultar' : 'Detalhar'}
                        </Button>
                      </td>
                    </tr>
                    {expandedService === service.name && (
                      <tr className="cloud-exec__detail-row">
                        <td colSpan={6}>
                          {renderServiceResources(service)}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="cloud-exec__section cloud-exec__print-section cloud-exec__print-break-before">
        <div className="cloud-exec__section-head">
          <div>
            <h2 className="cloud-exec__section-title">Inventário e Análise Detalhada</h2>
            <div className="cloud-exec__section-subtitle">
              Recursos, alertas e recomendacoes classificados por provider com rastreabilidade da origem dos dados.
            </div>
          </div>
          {['aws', 'azure', 'gcp'].includes(presentation.provider) && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button onClick={() => openInventory()}>
                Ver inventário completo
              </Button>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                loading={isExportingInventory}
                onClick={exportInventory}
              >
                Exportar inventário CSV
              </Button>
            </div>
          )}
        </div>

        <div className="cloud-exec__grid-2">
          <div className="cloud-exec__panel">
            <h3 style={{ marginBottom: 16 }}>Inventário Geral</h3>
            <div className="cloud-exec__table-wrap">
              <table className="cloud-exec__table">
                <thead>
                  <tr>
                    <th>Tipo de Recurso</th>
                    <th className="numeric">Quantidade</th>
                  </tr>
                </thead>
                <tbody>
                  {presentation.inventoryRows.map(row => (
                    <tr
                      key={row.name}
                      className={row.count > 0 ? 'cloud-exec__clickable-row' : undefined}
                      onClick={row.count > 0
                        ? () => openInventory(inventoryAssetTypes[row.name])
                        : undefined}
                    >
                      <td>
                        {row.name}
                        {row.count > 0 && (
                          <small className="gcp-inventory__secondary">Clique para ver nomes e características</small>
                        )}
                      </td>
                      <td className="numeric">{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="cloud-exec__panel">
            <h3 style={{ marginBottom: 16 }}>Resumo Executivo</h3>
            <div className="cloud-exec__list">
              <div className="cloud-exec__resource-card">
                <div className="cloud-exec__resource-head">
                  <div>
                    <div className="cloud-exec__resource-name">
                      {presentation.provider === 'gcp' ? 'Economia oficial identificada mensal' : 'Economia mensal potencial'}
                    </div>
                    <div className="cloud-exec__resource-type">
                      {presentation.provider === 'gcp'
                        ? 'Recomendações oficiais Google; cenários não validados ficam separados'
                        : 'Base atual de recomendacoes do sistema'}
                    </div>
                  </div>
                  <span className="cloud-exec__badge success">
                    <DollarOutlined />&nbsp;{numberFormatter(
                      presentation.executiveSummary.netMonthlySavings,
                      presentation.provider
                    )}
                  </span>
                </div>
              </div>

              <div className="cloud-exec__resource-card">
                <div className="cloud-exec__resource-head">
                  <div>
                    <div className="cloud-exec__resource-name">Recursos ociosos</div>
                    <div className="cloud-exec__resource-type">Itens com possibilidade de remocao ou consolidacao</div>
                  </div>
                  <span className="cloud-exec__badge warning">
                    <ExclamationCircleOutlined />&nbsp;{presentation.executiveSummary.idleTotal}
                  </span>
                </div>
              </div>

              <div className="cloud-exec__resource-card">
                <div className="cloud-exec__resource-head">
                  <div>
                    <div className="cloud-exec__resource-name">Alertas de seguranca</div>
                    <div className="cloud-exec__resource-type">Itens de governanca e postura operacional</div>
                  </div>
                  <span className="cloud-exec__badge danger">
                    <SafetyOutlined />&nbsp;{presentation.executiveSummary.securityTotal}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {presentation.tabs.length > 0 && (
          <>
            <div className="cloud-exec__tabs" style={{ marginTop: 16 }}>
              {presentation.tabs.map(tab => (
                <button
                  key={tab.key}
                  type="button"
                  className={`cloud-exec__tab${activeSection?.key === tab.key ? ' active' : ''}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="cloud-exec__tab-panel">
              <h3 style={{ marginBottom: 6 }}>{activeSection?.label}</h3>
              <div className="cloud-exec__section-subtitle" style={{ marginBottom: 16 }}>
                {activeSection?.subtitle}
              </div>
              <div className="cloud-exec__list">
                {activeSection?.items.map(item => (
                  <div key={`${activeSection.key}-${item.name}-${item.type}`} className="cloud-exec__resource-card">
                    <div className="cloud-exec__resource-head">
                      <div>
                        <div className="cloud-exec__resource-name">{item.name}</div>
                        <div className="cloud-exec__resource-type">
                          {item.type}
                          {item.meta ? ` • ${item.meta}` : ''}
                        </div>
                      </div>
                      <div className="cloud-exec__badge-group">
                        {item.sourceLabel && (
                          <span className={`cloud-exec__badge ${item.sourceTone || 'info'}`}>{item.sourceLabel}</span>
                        )}
                        <span className={`cloud-exec__badge ${item.badgeTone}`}>{item.badge}</span>
                      </div>
                    </div>
                    <div className="cloud-exec__resource-description">{item.description}</div>
                    {renderAffectedResources(item)}
                    {item.cost && (
                      <div className="cloud-exec__resource-description">
                        <CheckCircleOutlined /> {item.cost}
                      </div>
                    )}
                    {renderFeedbackControl(item, activeSection?.label)}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </section>

      <Drawer
        title={activeDrilldown?.title}
        open={!!activeDrilldown}
        onClose={() => setDrilldownKey(null)}
        width="min(560px, 94vw)"
      >
        {activeDrilldown && (
          <div className="cloud-exec__drawer">
            <div className="cloud-exec__drawer-subtitle">{activeDrilldown.subtitle}</div>
            {activeDrilldown.items.length ? (
              <div className="cloud-exec__list">
                {activeDrilldown.items.map(item => (
                  <div key={`${activeDrilldown.title}-${item.name}-${item.type}`} className="cloud-exec__resource-card">
                    <div className="cloud-exec__resource-head">
                      <div>
                        <div className="cloud-exec__resource-name">{item.name}</div>
                        <div className="cloud-exec__resource-type">
                          {item.type}
                          {item.meta ? ` • ${item.meta}` : ''}
                        </div>
                      </div>
                      <span className={`cloud-exec__badge ${item.badgeTone || 'info'}`}>{item.badge}</span>
                    </div>
                    <div className="cloud-exec__resource-description">{item.description}</div>
                    {renderAffectedResources(item)}
                    {item.cost && (
                      <div className="cloud-exec__resource-description">
                        <CheckCircleOutlined /> {item.cost}
                      </div>
                    )}
                    {renderFeedbackControl(item, (item as any).sectionLabel || activeDrilldown.title)}
                  </div>
                ))}
              </div>
            ) : (
              <Empty description={activeDrilldown.empty} />
            )}
          </div>
        )}
      </Drawer>
      <GcpInventoryDrawer
        provider={presentation.provider}
        accountId={presentation.rawData?.cloudAccountId}
        reportId={presentation.rawData?.reportId}
        open={inventoryOpen}
        initialAssetType={inventoryAssetType}
        initialProjectId={inventoryProject?.id}
        initialProjectName={inventoryProject?.name}
        onClose={() => {
          setInventoryOpen(false);
          setInventoryAssetType(undefined);
          setInventoryProject(null);
        }}
      />
    </div>
  );
};

export default CloudExecutiveLayout;
